/**
 * DocPdf — inline PDF embed node for PolicyEditor (TipTap). Same never-store-a-raw-url
 * principle as DocImage (PolicyEditorImage.ts): the documents bucket is private, so stored
 * content only ever carries `data-doc-pdf-id` (a document row created via the existing
 * upload-url/confirm flow, entityType 'document-pdf') and `data-doc-pdf-name`; the backend
 * sanitiser enforces the same shape on save (see _safe_pdf_link_attrs in prod-nimbus-documents).
 *
 * Reuses the <a> tag rather than inventing a new one - the sanitiser already has a dedicated,
 * audited attribute-stripping branch for <a>, so giving the PDF variant its own marker
 * attribute (checked before the normal href logic) is a smaller, better-understood change than
 * teaching the sanitiser a whole new tag. The rendered element never carries an href, so it's
 * never a real link - clicking it is handled entirely by JS, exactly like DocImage's <img>
 * never carries a src the browser could load on its own.
 *
 * Displayed as a small preview card (icon + filename), not the PDF itself inline - clicking it
 * opens a full viewer in a modal. The same two-tier cache / domRegistry pattern as DocImage
 * resolves data-doc-pdf-id to a live URL client-side; resolving it doesn't paint anything
 * (there's no <img src> equivalent to set) so it's stashed on the card's dataset instead, read
 * back only when the card is actually clicked.
 */
import { Node, mergeAttributes } from '@tiptap/core';
import type { Editor } from '@tiptap/core';

export interface DocPdfStorage {
  resolvedUrls: Map<string, string | null>;
  blobUrls: Map<string, string>;
  domRegistry: Map<string, Set<HTMLElement>>;
}

declare module '@tiptap/core' {
  interface Storage {
    docPdf: DocPdfStorage;
  }
}

/** The card's static contents (icon + filename + hint) - shared by the editor's node view and
 * RichTextView's read-view pass so the two never drift apart visually. Clears any existing
 * children first: the node view's <a> is always freshly created and empty, but RichTextView
 * calls this over an <a data-doc-pdf-id> straight out of dangerouslySetInnerHTML, which - unlike
 * the void <img> the image variant uses - can carry arbitrary text between its tags (the
 * sanitiser strips every nested tag but has no reason to reject plain text there). Left
 * unattended, that text would sit alongside the card's own icon/filename as a stray, uncleared
 * child. */
export function buildPdfCardContent(card: HTMLElement, fileName: string) {
  card.replaceChildren();
  const icon = document.createElement('span');
  icon.className = 'np-doc-pdf-icon';
  icon.innerHTML = '<i class="ri-file-pdf-2-line"></i>';
  const info = document.createElement('span');
  info.className = 'np-doc-pdf-info';
  const nameEl = document.createElement('span');
  nameEl.className = 'np-doc-pdf-name';
  nameEl.textContent = fileName;
  const hint = document.createElement('span');
  hint.className = 'np-doc-pdf-hint';
  hint.textContent = 'Click to view';
  info.append(nameEl, hint);
  card.append(icon, info);
}

/** Card visual state for a resolved (or not-yet-resolved, or unresolvable) url - the resolved
 * url itself lives only on the card's own dataset, read back on click, since there's no visible
 * src-equivalent to set eagerly. */
function applyPdfCardState(card: HTMLElement, url: string | null | undefined) {
  if (url) {
    card.dataset.resolvedUrl = url;
    card.classList.remove('np-doc-pdf-pending', 'np-doc-pdf-broken');
  } else if (url === null) {
    delete card.dataset.resolvedUrl;
    card.classList.remove('np-doc-pdf-pending');
    card.classList.add('np-doc-pdf-broken');
  } else {
    card.classList.add('np-doc-pdf-pending');
  }
}

/** Wires the click (and keyboard-equivalent) that opens the full-viewer modal - shared so the
 * editor node view and the read-view pass open the exact same modal in the exact same way. */
function wirePdfCardOpen(card: HTMLElement, fileName: string) {
  const open = () => {
    const url = card.dataset.resolvedUrl;
    if (url) openPdfPreviewModal(url, fileName);
  };
  card.addEventListener('click', open);
  card.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      open();
    }
  });
}

/** Full-viewer lightbox for a resolved PDF url. Plain DOM, not a React portal - both call
 * sites (a TipTap node view and a post-render DOM pass over dangerouslySetInnerHTML output)
 * live outside the React tree, the same reason DocImage's own src updates bypass React/
 * ProseMirror transactions entirely. Browsers render a PDF natively inside an iframe, so no
 * PDF.js dependency is needed. */
export function openPdfPreviewModal(url: string, fileName: string) {
  const backdrop = document.createElement('div');
  backdrop.className = 'np-pdf-modal-backdrop';

  const panel = document.createElement('div');
  panel.className = 'np-pdf-modal-panel';

  const header = document.createElement('div');
  header.className = 'np-pdf-modal-header';
  const title = document.createElement('span');
  title.className = 'np-pdf-modal-title';
  title.textContent = fileName;
  const closeBtn = document.createElement('button');
  closeBtn.type = 'button';
  closeBtn.className = 'np-pdf-modal-close';
  closeBtn.setAttribute('aria-label', 'Close');
  closeBtn.innerHTML = '<i class="ri-close-line"></i>';
  header.append(title, closeBtn);

  const iframe = document.createElement('iframe');
  iframe.className = 'np-pdf-modal-frame';
  iframe.src = url;
  iframe.title = fileName;

  panel.append(header, iframe);
  backdrop.appendChild(panel);

  const close = () => {
    document.removeEventListener('keydown', onKeyDown);
    backdrop.remove();
  };
  const onKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Escape') close();
  };
  backdrop.addEventListener('click', (e) => {
    if (e.target === backdrop) close();
  });
  closeBtn.addEventListener('click', close);
  document.addEventListener('keydown', onKeyDown);

  document.body.appendChild(backdrop);
}

export const DocPdf = Node.create<Record<string, never>, DocPdfStorage>({
  name: 'docPdf',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      docPdfId: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-doc-pdf-id'),
        renderHTML: (attributes: { docPdfId?: string | null }) =>
          attributes.docPdfId ? { 'data-doc-pdf-id': attributes.docPdfId } : {},
      },
      fileName: {
        default: null,
        parseHTML: (element: HTMLElement) => element.getAttribute('data-doc-pdf-name'),
        renderHTML: (attributes: { fileName?: string | null }) =>
          attributes.fileName ? { 'data-doc-pdf-name': attributes.fileName } : {},
      },
    };
  },

  parseHTML() {
    return [{ tag: 'a[data-doc-pdf-id]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['a', mergeAttributes(HTMLAttributes)];
  },

  addStorage() {
    return {
      resolvedUrls: new Map(),
      blobUrls: new Map(),
      domRegistry: new Map(),
    };
  },

  addNodeView() {
    return ({ node, editor }) => {
      const card = document.createElement('a');
      card.className = 'np-doc-pdf-card';
      card.setAttribute('role', 'button');
      card.tabIndex = 0;
      const docPdfId = node.attrs.docPdfId as string | null;
      const fileName = (node.attrs.fileName as string | null) || 'Document.pdf';
      buildPdfCardContent(card, fileName);
      wirePdfCardOpen(card, fileName);

      const storage = editor.storage.docPdf;
      if (docPdfId) {
        if (!storage.domRegistry.has(docPdfId)) storage.domRegistry.set(docPdfId, new Set());
        storage.domRegistry.get(docPdfId)!.add(card);
        const cached = storage.blobUrls.get(docPdfId) ?? storage.resolvedUrls.get(docPdfId);
        applyPdfCardState(card, cached);
      } else {
        card.classList.add('np-doc-pdf-broken');
      }

      return {
        dom: card,
        destroy: () => {
          if (docPdfId) storage.domRegistry.get(docPdfId)?.delete(card);
        },
      };
    };
  },
});

/** Every data-doc-pdf-id in a stored content string - what needs resolving before display. */
export function extractPdfIds(html: string): string[] {
  const ids = new Set<string>();
  const re = /data-doc-pdf-id="([0-9a-fA-F-]{36})"/g;
  let match: RegExpExecArray | null;
  while ((match = re.exec(html))) ids.add(match[1]);
  return Array.from(ids);
}

/** Push freshly-resolved urls into an editor's PDF storage and update any already-mounted
 * cards for them directly. */
export function registerResolvedPdfUrls(editor: Editor, urls: Record<string, string | null>) {
  const storage = editor.storage.docPdf;
  if (!storage) return;
  for (const [id, url] of Object.entries(urls)) {
    storage.resolvedUrls.set(id, url);
    if (storage.blobUrls.has(id)) continue; // a live local preview always wins
    storage.domRegistry.get(id)?.forEach((card) => applyPdfCardState(card, url));
  }
}

/** Register an instant local preview for a PDF the user just picked, before its upload to S3
 * has even finished - the browser already has the bytes, so the card is clickable immediately
 * rather than waiting on a round trip. */
export function registerBlobPdfUrl(editor: Editor, id: string, blobUrl: string) {
  const storage = editor.storage.docPdf;
  if (!storage) return;
  storage.blobUrls.set(id, blobUrl);
  storage.domRegistry.get(id)?.forEach((card) => applyPdfCardState(card, blobUrl));
}

/** RichTextView's read-view equivalent of the node view above: build the card's visible
 * contents and click behaviour over an already-mounted <a data-doc-pdf-id> from
 * dangerouslySetInnerHTML output, then resolve+apply its url the same way. Exported as one
 * step (rather than the three editor-side pieces) because the read view has no ProseMirror
 * node lifecycle to hook into - it runs once per render, over whichever elements exist. */
export function hydratePdfCard(card: HTMLAnchorElement) {
  const fileName = card.getAttribute('data-doc-pdf-name') || 'Document.pdf';
  card.classList.add('np-doc-pdf-card');
  card.setAttribute('role', 'button');
  card.tabIndex = 0;
  buildPdfCardContent(card, fileName);
  wirePdfCardOpen(card, fileName);
  applyPdfCardState(card, undefined);
}

export { applyPdfCardState };
